// AI-assisted: Claude 2026-08-08, human-reviewed: ausstehend
// Optionen → Überblick (nur Admins). Zwei Quellen:
//  - Betriebszahlen (Konten, Pläne, Übungen) aus dem normalen Lesepfad
//  - Aktivität als ANONYMES Server-Aggregat (pb_hooks/aktivitaet.pb.js):
//    nur Summen, nie wer. Fremde Trainingseinträge und Körpergewichte bleiben
//    unsichtbar — auch für die Verwaltung. Der Admin ist kein Trainer,
//    sondern der Hausmeister.
import { useEffect, useState } from "react"
import { pb } from "@/lib/pb"
import { getExercises, getPlans } from "@/lib/data"
import { ladeMitglieder } from "@/lib/mitglieder"
import { fmtDate } from "@/lib/dates"

interface Zahlen {
  konten: number
  offeneStartpasswoerter: number
  kuratoren: number
  vorlagen: number
  eigenePlaene: number
  eigeneUebungen: number
}

/** Vom Server gerechnet — nur Summen, nie wer (siehe pb_hooks/aktivitaet.pb.js) */
interface Aktivitaet {
  konten: number
  angekommen: number
  aktive7: number
  aktive30: number
  trainingstage7: number
  trainingstage30: number
  letzteAktivitaet: string
}

export default function AdminUebersicht() {
  const [z, setZ] = useState<Zahlen | null>(null)
  const [akt, setAkt] = useState<Aktivitaet | null>(null)

  useEffect(() => {
    const laden = async () => {
      const [mitglieder, plaene, uebungen] = await Promise.all([
        ladeMitglieder(), getPlans(), getExercises(),
      ])
      setZ({
        konten: mitglieder.length,
        offeneStartpasswoerter: mitglieder.filter((m) => m.must_change_password).length,
        kuratoren: mitglieder.filter((m) => m.may_curate || m.role === "admin").length,
        vorlagen: plaene.filter((p) => !p.owner).length,
        eigenePlaene: plaene.filter((p) => p.owner).length,
        eigeneUebungen: uebungen.filter((e) => e.source === "custom").length,
      })
      // Aktivität rechnet der Server und gibt nur Summen zurück
      try {
        setAkt(await pb.send<Aktivitaet>("/api/fithub/aktivitaet", { method: "GET" }))
      } catch { /* offline — der Rest der Übersicht steht trotzdem */ }
    }
    // Fehler nicht verschlucken: sonst bliebe der ganze Überblick still leer
    laden().catch((e) => { console.error("[admin-uebersicht]", e) })
  }, [])

  if (!z) return null

  return (
    <>
      <h2>Überblick</h2>
      <div className="statgrid">
        <div className="stat">
          <span className="stat-lbl">Konten</span>
          <b className="num-hero">{z.konten}</b>
        </div>
        <div className="stat">
          <span className="stat-lbl">Startpasswort offen</span>
          <b className="num-hero">{z.offeneStartpasswoerter}</b>
        </div>
        <div className="stat">
          <span className="stat-lbl">Katalog-Pflege</span>
          <b className="num-hero">{z.kuratoren}</b>
        </div>
      </div>

      <div className="statgrid" style={{ marginTop: 14 }}>
        <div className="stat">
          <span className="stat-lbl">Vorlagen</span>
          <b className="num-hero">{z.vorlagen}</b>
        </div>
        <div className="stat">
          <span className="stat-lbl">Eigene Pläne</span>
          <b className="num-hero">{z.eigenePlaene}</b>
        </div>
        <div className="stat">
          <span className="stat-lbl">Eigene Übungen</span>
          <b className="num-hero">{z.eigeneUebungen}</b>
        </div>
      </div>

      {akt && (
        <>
          <h2>Aktivität (anonym)</h2>
          <div className="statgrid">
            <div className="stat">
              <span className="stat-lbl">Angekommen</span>
              <b className="num-hero">{akt.angekommen}<i>/{akt.konten}</i></b>
            </div>
            <div className="stat">
              <span className="stat-lbl">Aktiv (7 Tage)</span>
              <b className="num-hero">{akt.aktive7}</b>
            </div>
            <div className="stat">
              <span className="stat-lbl">Aktiv (30 Tage)</span>
              <b className="num-hero">{akt.aktive30}</b>
            </div>
          </div>
          <div className="statgrid statgrid--2" style={{ marginTop: 14 }}>
            <div className="stat">
              <span className="stat-lbl">Trainingstage (30)</span>
              <b className="num-hero">{akt.trainingstage30}</b>
            </div>
            <div className="stat">
              <span className="stat-lbl">Zuletzt trainiert</span>
              <b className="num-hero stat-date">{akt.letzteAktivitaet ? fmtDate(akt.letzteAktivitaet) : "–"}</b>
            </div>
          </div>
          <p className="hint">
            {akt.aktive7 === 0
              ? "Diese Woche war noch niemand trainieren."
              : `${akt.aktive7} von ${akt.angekommen} angemeldeten Mitgliedern waren diese Woche trainieren.`}
          </p>
        </>
      )}

      <p className="hint" style={{ maxWidth: "62ch" }}>
        Diese Zahlen rechnet der Server und gibt nur Summen heraus – wer wann trainiert hat,
        welche Übungen oder Gewichte, steht nirgends. Trainingseinträge und Körpergewichte
        bleiben privat, auch für dich.
      </p>
    </>
  )
}
